import React from 'react'
import { Dotsmenu } from '@e3dc-react/icons'
import { Box, IconButton, Menu, MenuItem } from '@mui/material'
import { TariffModel } from '../models/tariffs-model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { useHagerBridgePermissions } from '../../shared'
import { TariffCategory, TariffStatusType } from '../../shared/models/types'
import { TariffDetailsModel } from '../models/single-tariff-model'

interface TariffsTableMenuIconProps {
  tariff: TariffModel
  installationId: number
  dynamicTariffActivated: boolean
}

/**
 * Tariffs 3 dot menu icon component which holds edit and delete tariff menu options
 * @param tariffs - single tariff object as TariffModel
 * @param installationId - unique identifier for installation
 */
export const TariffsTableMenuIcon: React.FC<TariffsTableMenuIconProps> = ({ tariff, installationId, dynamicTariffActivated }): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)

  const { t } = useCustomTranslation()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const { openDeleteTariffDialog, openEditTariffDialog } = useModals()
  const {
    permissions: { tariffPermissions },
  } = useHagerBridgePermissions()

  const isDeleteDynamicTariffDisabled =
    tariff.tariffCategory === TariffCategory.dynamicTariff && (dynamicTariffActivated || tariff.tariffStatus === TariffStatusType.active)

  const tariffDetailsForEdit: TariffDetailsModel = {
    tariffName: tariff.tariffName,
    tariffCategory: tariff.tariffCategory,
    tariffStatus: tariff.tariffStatus,
    currencyCode: tariff.currencyCode,
    dischargePrice: tariff.dischargePrice ?? 0,
    dynamicPrices: tariff.dynamicPrices,
    endTime: tariff.endTime ?? undefined,
    lastUpdated: tariff.lastUpdated,
    solarPrice: tariff.staticPrices.solarPrice ?? 0,
    startTime: tariff.startTime ?? undefined,
    staticGridPrice: tariff.staticPrices.gridPrice,
  }

  return (
    <>
      <Box id={`tariff-table-row-dots-menu`}>
        <IconButton onClick={handleClick}>
          <Dotsmenu />
        </IconButton>
      </Box>
      <Menu anchorEl={anchorEl} open={isOpen} onClose={handleClose} onClick={event => event.stopPropagation()}>
        {tariffPermissions.editTariff && (
          <MenuItem
            onClick={event => {
              event.stopPropagation()
              handleClose()
              openEditTariffDialog({ tariff: tariffDetailsForEdit, installationId, tariffId: tariff.tariffId })
            }}
          >
            {t('emobility.tariffs.editTariff')}
          </MenuItem>
        )}
        {tariffPermissions.deleteTariff && (
          <MenuItem
            disabled={isDeleteDynamicTariffDisabled}
            onClick={event => {
              event.stopPropagation()
              openDeleteTariffDialog({ installationId, tariffId: tariff.tariffId })
              handleClose()
            }}
          >
            {t('emobility.tariff.deleteTariff')}
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
